import { supabase } from '@/integrations/supabase/client';
import { normalizeOpenLibraryId } from '@/lib/searchUtils';

export interface BookIdentifiers {
  id?: string;
  isbn13?: string;
  isbn10?: string;
  open_library_id?: string;
}

export interface BookMetadata {
  title: string;
  subtitle?: string;
  authors?: string[];
  cover_url?: string;
  published_year?: number;
  synopsis?: string;
  page_count?: number;
}

export type BookInput = BookIdentifiers & BookMetadata;

/**
 * Check if a value is a valid UUID
 */
export function isValidUuid(id: string | undefined | null): boolean {
  if (!id) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}

/**
 * Find an existing book by ISBN (priority) or OpenLibrary ID
 * Returns the book ID if found, null otherwise
 */
export async function findExistingBook(book: BookIdentifiers & Partial<BookMetadata>): Promise<string | null> {
  // Priority 1: ISBN13
  if (book.isbn13) {
    const { data } = await supabase
      .from('books')
      .select('id')
      .or(`isbn13.eq.${book.isbn13},canonical_isbn13.eq.${book.isbn13}`)
      .maybeSingle();
    if (data?.id) return data.id;
  }

  // Priority 2: ISBN10
  if (book.isbn10) {
    const { data } = await supabase
      .from('books')
      .select('id')
      .eq('isbn10', book.isbn10)
      .maybeSingle();
    if (data?.id) return data.id;
  }

  // Priority 3: OpenLibrary ID
  if (book.open_library_id) {
    const normalizedId = normalizeOpenLibraryId(book.open_library_id);
    if (normalizedId) {
      const { data } = await supabase
        .from('books')
        .select('id')
        .or(`open_library_id.eq.${normalizedId},open_library_id.eq./works/${normalizedId}`)
        .maybeSingle();
      if (data?.id) return data.id;
    }
  }

  // Priority 4: Title + Author fuzzy match (for books without identifiers)
  if (book.title && book.authors && book.authors.length > 0) {
    const normalizedTitle = book.title.toLowerCase().trim();
    const primaryAuthor = book.authors[0].toLowerCase().trim();
    
    // Use ilike for case-insensitive matching
    const { data } = await supabase
      .from('books')
      .select('id, title, authors')
      .ilike('title', normalizedTitle)
      .limit(10);
    
    if (data && data.length > 0) {
      // Find exact match on author
      const match = data.find(b => 
        b.authors?.some(a => a.toLowerCase().trim() === primaryAuthor)
      );
      if (match) return match.id;
    }
  }

  return null;
}

/**
 * Create a new book with proper provenance (ISBN-first)
 * Returns the new book ID
 */
export async function createBook(book: BookInput): Promise<string> {
  const hasIsbn = book.isbn13 || book.isbn10;
  const hasOlid = book.open_library_id;
  
  const normalizedOlid = hasOlid ? normalizeOpenLibraryId(book.open_library_id!) : null;
  
  const insertData = {
    title: book.title || 'Untitled',
    subtitle: book.subtitle || null,
    authors: book.authors || [],
    cover_url: book.cover_url || null,
    published_year: book.published_year || null,
    synopsis: book.synopsis || null,
    page_count: book.page_count || null,
    isbn13: book.isbn13 || null,
    isbn10: book.isbn10 || null,
    canonical_isbn13: book.isbn13 || null,
    open_library_id: normalizedOlid,
    source: hasIsbn ? 'isbndb' : (hasOlid ? 'openlibrary' : null),
    primary_source: hasIsbn ? 'isbndb' : (hasOlid ? 'openlibrary' : null),
    source_confidence: hasIsbn ? 95 : (hasOlid ? 70 : null),
    identity_locked: hasIsbn ? true : null,
    language: 'en',
    enrichment_status: 'pending',
  };

  const { data: newBook, error } = await supabase
    .from('books')
    .insert(insertData)
    .select('id')
    .single();

  if (error) throw error;
  if (!newBook?.id) throw new Error('Failed to create book');

  return newBook.id;
}

/**
 * Find or create a book using ISBN-first lookup pattern
 * Returns the book ID (existing or newly created)
 */
export async function findOrCreateBook(book: BookInput): Promise<string> {
  // If we have a valid UUID already, return it
  if (book.id && isValidUuid(book.id)) {
    return book.id;
  }

  // Validate we have at least one way to identify the book
  const hasIdentifier = book.isbn13 || book.isbn10 || book.open_library_id;
  const hasTitleAuthor = book.title && book.authors && book.authors.length > 0;
  
  if (!hasIdentifier && !hasTitleAuthor) {
    throw new Error('No valid book identifier available (ISBN, OpenLibrary ID, or Title+Author required)');
  }

  // Try to find existing book (now includes title+author fallback)
  const existingId = await findExistingBook(book);
  if (existingId) {
    return existingId;
  }

  // Create new book
  return createBook(book);
}

/**
 * Check if a book has any valid identifier
 */
export function hasValidIdentifier(book: BookIdentifiers & Partial<BookMetadata>): boolean {
  const hasUuid = !!(book.id && isValidUuid(book.id));
  const hasIsbn = !!book.isbn13 || !!book.isbn10;
  const hasOlid = !!book.open_library_id;
  const hasTitleAuthor = !!(book.title && book.authors && book.authors.length > 0);
  
  return hasUuid || hasIsbn || hasOlid || hasTitleAuthor;
}
